import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "@/pages/About";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { AppStatusBadge, StatusBadge, PaymentBadge } from "@/components/Badges";
import { adminApi } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, FileStack, Clock, CheckCircle2, Award, Bell, Send, Mail, Users, Ticket } from "lucide-react";

const StatCard = ({ icon: Icon, label, value }) => (
  <Card><CardContent className="flex items-center gap-4 p-5">
    <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-wgreen-100"><Icon className="h-5 w-5 text-wgreen-700" /></div>
    <div><p className="font-heading text-2xl font-semibold tabular text-navy-900">{value}</p><p className="text-sm text-[var(--wechr-muted-text)]">{label}</p></div>
  </CardContent></Card>
);

const FILTERS = [
  { v: "all", l: "All" }, { v: "submitted", l: "Submitted" }, { v: "in_review", l: "In Review" },
  { v: "needs_info", l: "Needs Info" }, { v: "enhanced_review", l: "Enhanced Review" },
  { v: "approved", l: "Approved" }, { v: "rejected", l: "Rejected" }, { v: "certified", l: "Certified" }, { v: "draft", l: "Draft" },
];

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [apps, setApps] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [expiring, setExpiring] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [reminding, setReminding] = useState(null);

  const load = async (f) => {
    setLoading(true);
    try {
      const [s, a, e, n] = await Promise.all([
        adminApi.stats(),
        adminApi.listApplications(f === "all" ? undefined : f),
        adminApi.expiringCerts(),
        adminApi.notifications(),
      ]);
      setStats(s.data); setApps(a.data);
      setExpiring(e.data.certificates || []);
      setNotifications(n.data.notifications || []);
    } finally { setLoading(false); }
  };

  const sendReminder = async (certId) => {
    setReminding(certId);
    try {
      const { data } = await adminApi.sendReminder(certId);
      const st = data.notification?.status;
      if (st === "sent") toast.success("Reminder email sent");
      else toast.message("Reminder logged", { description: `Delivery status: ${st}. ${data.notification?.detail || ""}` });
      await load(filter);
    } catch { toast.error("Could not send reminder"); } finally { setReminding(null); }
  };

  useEffect(() => { load(filter); /* eslint-disable-next-line */ }, [filter]);

  return (
    <div>
      <PageHeader eyebrow="Admin" title="Review Dashboard" subtitle="Review applications, verify documents, adjust scores, and issue certifications." />
      <section className="wechr-section">
        <div className="wechr-container">
          <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
            <h2 className="font-heading text-lg font-semibold text-navy-900">Overview</h2>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={() => navigate("/admin/promo-codes")} data-testid="admin-promo-codes-button"><Ticket className="mr-2 h-4 w-4" /> Promotion codes</Button>
              <Button variant="outline" onClick={() => navigate("/admin/users")} data-testid="admin-manage-users-button"><Users className="mr-2 h-4 w-4" /> Manage user accounts</Button>
            </div>
          </div>
          {stats && (
            <div className="mb-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
              <StatCard icon={FileStack} label="Total applications" value={stats.total_applications} />
              <StatCard icon={Clock} label="Awaiting review" value={stats.submitted + stats.in_review} />
              <StatCard icon={CheckCircle2} label="Certified" value={stats.certified} />
              <StatCard icon={Award} label="Certificates issued" value={stats.certificates} />
              <button onClick={() => navigate("/admin/users")} className="text-left" data-testid="admin-users-stat">
                <StatCard icon={Users} label="User accounts" value={stats.users ?? "—"} />
              </button>
            </div>
          )}

          <Card>
            <CardContent className="p-0">
              <div className="flex items-center justify-between gap-3 border-b border-[var(--wechr-border)] p-4">
                <h3 className="font-heading text-base font-semibold text-navy-900">Applications</h3>
                <div className="w-48">
                  <Select value={filter} onValueChange={setFilter}>
                    <SelectTrigger data-testid="admin-applications-filter"><SelectValue /></SelectTrigger>
                    <SelectContent>{FILTERS.map((f) => <SelectItem key={f.v} value={f.v}>{f.l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              {loading ? (
                <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-wgreen-600" /></div>
              ) : apps.length === 0 ? (
                <p className="py-16 text-center text-sm text-[var(--wechr-muted-text)]">No applications match this filter.</p>
              ) : (
                <div className="overflow-x-auto">
                  <Table data-testid="admin-applications-table">
                    <TableHeader>
                      <TableRow className="[&_th]:text-xs [&_th]:uppercase [&_th]:tracking-wide">
                        <TableHead>Company</TableHead><TableHead>Industry</TableHead><TableHead>Country</TableHead>
                        <TableHead>AI Score</TableHead><TableHead>Payment</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {apps.map((a) => (
                        <TableRow key={a.id} data-testid="admin-applications-row">
                          <TableCell className="font-medium text-navy-900">{a.company_name}</TableCell>
                          <TableCell className="text-[var(--wechr-muted-text)]">{a.industry}</TableCell>
                          <TableCell className="text-[var(--wechr-muted-text)]">{a.country}</TableCell>
                          <TableCell>{a.ai_review ? `${a.ai_review.overall_score}/100` : "—"}</TableCell>
                          <TableCell>{a.payment ? <PaymentBadge status={a.payment.status} /> : <span className="text-xs text-[var(--wechr-muted-text)]">—</span>}</TableCell>
                          <TableCell><AppStatusBadge status={a.status} /></TableCell>
                          <TableCell className="text-right"><Button size="sm" variant="outline" onClick={() => navigate(`/admin/applications/${a.id}`)} data-testid={`admin-review-open-${a.id}`}>Review</Button></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Expiring certificates */}
          <Card className="mt-6" data-testid="admin-expiring-certs">
            <CardContent className="p-0">
              <div className="flex items-center gap-2 border-b border-[var(--wechr-border)] p-4">
                <Bell className="h-4 w-4 text-gold-600" />
                <h3 className="font-heading text-base font-semibold text-navy-900">Expiring & expired certificates</h3>
              </div>
              {expiring.length === 0 ? (
                <p className="p-6 text-sm text-[var(--wechr-muted-text)]">No certificates are expiring within the reminder window.</p>
              ) : (
                <div className="divide-y divide-[var(--wechr-border)]">
                  {expiring.map((c) => (
                    <div key={c.certificate_id} className="flex flex-wrap items-center justify-between gap-3 p-4">
                      <div>
                        <p className="font-medium text-navy-900">{c.company_name} <span className="font-mono text-xs text-[var(--wechr-muted-text)]">{c.certificate_id}</span></p>
                        <p className="text-sm text-[var(--wechr-muted-text)]">{c.level} · expires {c.expiry_date} · {c.days_left >= 0 ? `${c.days_left} days left` : `expired ${-c.days_left} days ago`}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <StatusBadge status={c.status} />
                        <Button size="sm" variant="outline" onClick={() => sendReminder(c.certificate_id)} disabled={reminding === c.certificate_id} data-testid={`admin-send-reminder-${c.certificate_id}`}>
                          {reminding === c.certificate_id ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Send className="mr-1 h-4 w-4" />} Send reminder
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Email notifications log */}
          <Card className="mt-6" data-testid="admin-notifications-log">
            <CardContent className="p-0">
              <div className="flex items-center gap-2 border-b border-[var(--wechr-border)] p-4">
                <Mail className="h-4 w-4 text-wgreen-600" />
                <h3 className="font-heading text-base font-semibold text-navy-900">Email notifications</h3>
              </div>
              {notifications.length === 0 ? (
                <p className="p-6 text-sm text-[var(--wechr-muted-text)]">No notifications sent yet.</p>
              ) : (
                <div className="max-h-96 divide-y divide-[var(--wechr-border)] overflow-y-auto">
                  {notifications.slice(0, 50).map((n) => (
                    <div key={n.id} className="flex flex-wrap items-center justify-between gap-2 p-3 text-sm">
                      <div>
                        <p className="font-medium text-navy-900">{n.subject}</p>
                        <p className="text-xs text-[var(--wechr-muted-text)]">{n.kind} · to {n.to} · {new Date(n.created_at).toLocaleString()}</p>
                      </div>
                      <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${n.status === "sent" ? "bg-wgreen-100 text-wgreen-700" : n.status === "failed" ? "bg-[#FEE4E2] text-[#B42318]" : "bg-[#EEF2F8] text-navy-700"}`}>{n.status}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default AdminDashboard;
